import { UISDK } from "@/ui-sdk";
import { parseHandles } from "../utils";
import {
  processComplementaryRecommendations,
  processSimilarProductRecommendations,
} from "./func";

type ProductFetcher = (id: string) => Promise<any>;

const BLOCK_TYPES = {
  SIMILAR: "similar",
  COMPLEMENTARY: "complementary",
  MANUAL: "manual",
} as const;

type BlockType = (typeof BLOCK_TYPES)[keyof typeof BLOCK_TYPES];

const BLOCK_TITLES = {
  [BLOCK_TYPES.SIMILAR]: "RECOMMENDED",
  [BLOCK_TYPES.COMPLEMENTARY]: "Pair it with",
  [BLOCK_TYPES.MANUAL]: "Manual Products",
} as const;

async function fetchProducts(
  handles: string[],
  fetcher: ProductFetcher,
): Promise<any[]> {
  try {
    return await Promise.all(handles.map(fetcher));
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
}

async function processRecommendations(
  blockType: BlockType,
  products: any[],
): Promise<any[]> {
  switch (blockType) {
    case BLOCK_TYPES.SIMILAR:
      return processSimilarProductRecommendations({ products });
    case BLOCK_TYPES.COMPLEMENTARY:
      return processComplementaryRecommendations({ products });
    case BLOCK_TYPES.MANUAL:
      return products;
    default:
      throw new Error(`Unhandled block type: ${blockType}`);
  }
}

async function initCartInlineBlock(element: HTMLElement) {
  const blockType = element.getAttribute(
    "data-helium-cart-inline-block-type",
  ) as BlockType;

  if (!blockType || !Object.values(BLOCK_TYPES).includes(blockType)) {
    console.error(
      "Invalid or missing 'data-helium-cart-inline-block-type' attribute.",
    );
    return;
  }

  const cartItems = window.helium.datastore.current.cart?.items ?? [];
  const handles = cartItems?.map((item: any) => item.handle);

  // let productsToFetch: string[];
  // let productFetcher: ProductFetcher;

  // if (blockType === BLOCK_TYPES.MANUAL) {
  //   const handlesStr = element.getAttribute(
  //     "data-helium-cart-inline-block-product-handles",
  //   );
  //   if (!handlesStr) {
  //     console.error(
  //       "Missing 'data-helium-cart-inline-block-product-handles' attribute for manual block.",
  //     );
  //     return;
  //   }
  //   productsToFetch = parseHandles(handlesStr);
  //   productFetcher = (id) => window.helium.datastore.getProduct({ id });
  // } else {
  //   productsToFetch = handles;
  //   productFetcher =
  //     blockType === BLOCK_TYPES.SIMILAR
  //       ? (id) => window.helium.datastore.getSimilar({ id })
  //       : (id) => window.helium.datastore.getComplementary({ id });
  // }

  const [similarIds, complementaryIds] = await Promise.all([
    fetchProducts(handles, (id) => window.helium.datastore.getSimilar({ id })),
    fetchProducts(handles, (id) =>
      window.helium.datastore.getComplementary({ id }),
    ),
  ]);

  const [similarProducts, complementaryProducts] = await Promise.all([
    processRecommendations(BLOCK_TYPES.SIMILAR, similarIds),
    processRecommendations(
      BLOCK_TYPES.COMPLEMENTARY,
      complementaryIds
        .filter((item) => Object.keys(item).length > 0)
        .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    ),
  ]);

  console.log({
    similarProducts,
    complementaryProducts,
  });

  // let products;
  // if (blockType === BLOCK_TYPES.COMPLEMENTARY) {
  //   products = await fetchProducts(productsToFetch, productFetcher);
  //   products = products.reduce((acc, curr) => ({ ...acc, ...curr }), {});
  // } else {
  //   products = await fetchProducts(productsToFetch, productFetcher);
  // }
  // const recommendations = await processRecommendations(blockType, products);
  // console.log({ blockType, recommendations });

  // if (recommendations.length > 0) {
  //   window.helium.ui.renderInlineBlock(
  //     element,
  //     recommendations,
  //     BLOCK_TITLES[blockType],
  //   );
  // }

  if (similarProducts.length > 0 || complementaryProducts.length > 0) {
    window.helium.ui.renderCartInlineBlock({
      element,
      similarRecc: similarProducts.sort(
        (a, b) =>
          b.priceRange.minVariantPrice.amount -
          a.priceRange.minVariantPrice.amount,
      ),
      complementartyRecc: complementaryProducts,
      similarTitle: BLOCK_TITLES[BLOCK_TYPES.SIMILAR],
      complementartyTitle: BLOCK_TITLES[BLOCK_TYPES.COMPLEMENTARY],
    });
  }
}

function createCartWatcher(callback: () => void) {
  let previousHandles: string[] = [];

  const handleCartUpdate = (data: any) => {
    const currentHandles =
      window.helium.datastore.current.cart?.items?.map((item) => item.handle) ??
      [];
    if (JSON.stringify(currentHandles) !== JSON.stringify(previousHandles)) {
      previousHandles = currentHandles;
      callback();
    }
  };
  window.helium.events.subscribe({
    func: handleCartUpdate,
    filters: ["cart update"],
    key: "cartInlineBlockWatcher",
  });
  return () => {
    window.helium.events.unsubscribe({ key: "cartInlineBlockWatcher" });
  };
}

export default async function handleCartInlineBlock(
  element: HTMLElement,
  sdk?: UISDK,
) {
  const handleUpdate = () => initCartInlineBlock(element);
  handleUpdate();
  const cleanupWatcher = createCartWatcher(handleUpdate);
  return cleanupWatcher;
}
